import React, { useEffect, useMemo, useState } from 'react';
import AdminLayout from '../../layouts/AdminLayout';
import { Box, Heading, Button, SimpleGrid, FormControl, FormLabel, Input, HStack, VStack, Text, IconButton, useToast, Divider, Alert, AlertIcon, Badge, Checkbox, Image, Spinner, Link, Switch, useDisclosure, Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, ModalFooter, Tabs, TabList, TabPanels, Tab, TabPanel } from '@chakra-ui/react';
import { getAdminSettings, updateAdminSettings, AdminSettings } from '../../services/settings';
import { FiPlus, FiTrash2, FiSave } from 'react-icons/fi';
import { triggerPrefetch } from '../../services/admin/prefetch';
import { getCachedYouTube, YouTubeVideo } from '../../services/youtube';

export type AdminVideoItem = {
  url: string;
  title?: string;
  length?: string;
  uploaded_at?: string;
  thumbnail_url?: string;
};

//

const AdminVideosPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState<AdminVideoItem[]>([]);
  const videosSource: 'auto' = 'auto';
  const [videosEnabled, setVideosEnabled] = useState<boolean>(true);
  const toast = useToast();
  const { isOpen: isAddOpen, onOpen: onOpenAdd, onClose: onCloseAdd } = useDisclosure();

  // YouTube Scraper API integration state
  const [channelInput, setChannelInput] = useState<string>('');
  const [ytLoading, setYtLoading] = useState<boolean>(false);
  const [ytError, setYtError] = useState<string>('');
  const [ytVideos, setYtVideos] = useState<Array<{
    video_id: string;
    title: string;
    length?: string;
    thumbnail_url?: string;
    views?: number;
    views_text?: string;
    published_text?: string;
    published_date?: string;
  }>>([]);
  const [selectedIds, setSelectedIds] = useState<Record<string, boolean>>({});

  // Auto source preview state (cached YouTube)
  const [autoVideos, setAutoVideos] = useState<YouTubeVideo[]>([]);
  const [autoLoading, setAutoLoading] = useState<boolean>(false);
  const [autoError, setAutoError] = useState<string>('');
  const [filter, setFilter] = useState<string>('');
  // Title overrides for auto mode (video_id -> title)
  const [titleOverrides, setTitleOverrides] = useState<Record<string, string>>({});
  const [directUrl, setDirectUrl] = useState<string>('');
  const [didBackfill, setDidBackfill] = useState<boolean>(false);

  // Derived flags
  const hasChannel = useMemo(() => (channelInput || '').trim().length > 0, [channelInput]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const s: AdminSettings = await getAdminSettings();
        if (!mounted) return;
        const vids = Array.isArray((s as any).videos_items) ? (s as any).videos_items as AdminVideoItem[] : [];
        const legacy = Array.isArray((s as any).videos) ? ((s as any).videos as string[]).map((url) => ({ url })) : [];
        setItems(vids.length ? vids : legacy);
        // Force automatic source; manual editing is removed in favor of inline add/import
        // Default enable if not explicitly set and there are any videos configured
        const explicit = (s as any).videos_module_enabled;
        const hasAny = (vids.length + legacy.length) > 0;
        setVideosEnabled(typeof explicit === 'boolean' ? Boolean(explicit) : hasAny);
        // Prefill channel handle from settings if available (social/youtube_url)
        const ytUrl = (s as any).youtube_url || (s as any).social_youtube || '';
        if (ytUrl) setChannelInput(ytUrl);
        // Load existing overrides
        setTitleOverrides(((s as any).videos_title_overrides as any) || {});
      } catch (e) {
        // ignore
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const getTitleFromUrl = async (url: string): Promise<string | undefined> => {
    try {
      const u = `https://noembed.com/embed?url=${encodeURIComponent(url)}`;
      const res = await fetch(u, { method: 'GET' });
      if (!res.ok) return undefined;
      const data = await res.json();
      const t = (data?.title || '').toString().trim();
      return t || undefined;
    } catch {
      return undefined;
    }
  };

  // Load cached YouTube videos for preview
  useEffect(() => {
    let mounted = true;
    const run = async () => {
      if (loading) return;
      setAutoError('');
      setAutoLoading(true);
      try {
        const payload = await getCachedYouTube();
        if (!mounted) return;
        setAutoVideos(payload?.videos || []);
      } catch (err) {
        if (!mounted) return;
        setAutoError('Nepodařilo se načíst cache videí. Zkuste Aktualizovat.');
      } finally {
        if (mounted) setAutoLoading(false);
      }
    };
    run();
    return () => { mounted = false; };
  }, [loading]);

  useEffect(() => {
    if (loading || didBackfill) return;
    const missing = (items || []).filter(it => (it.url || '').trim().length > 0 && !(it.title || '').trim()).slice(0, 5);
    if (missing.length === 0) return;
    let canceled = false;
    const run = async () => {
      const next = [...items];
      let changed = false;
      for (const m of missing) {
        if (canceled) break;
        const t = await getTitleFromUrl(m.url);
        if (t) {
          const idx = next.findIndex(p => p.url === m.url);
          if (idx >= 0) { next[idx] = { ...next[idx], title: t }; changed = true; }
        }
      }
      if (!canceled && changed) {
        setItems(next);
        try { await updateAdminSettings({ videos_items: next, videos_module_enabled: videosEnabled }); } catch {}
      }
      if (!canceled) setDidBackfill(true);
    };
    run();
    return () => { canceled = true; };
  }, [loading, items, videosEnabled, didBackfill]);

  type PreviewItem = {
    key: string;
    title: string;
    thumbnail_url?: string;
    published_date?: string;
    video_id?: string;
    source: 'manual'|'auto';
    url?: string;
  };

  // Combined preview for AUTO mode: manual + auto (dedup), filtered by title, ordered by date desc
  const combinedAutoPreview = useMemo(() => {
    const manual: PreviewItem[] = (items || []).filter(it => (it.url || '').trim().length > 0).map((it, idx) => {
      let id: string | undefined;
      try {
        const u = (it.url || '').trim();
        if (u.includes('youtu.be/')) {
          id = u.split('youtu.be/')[1]?.split(/[?&#]/)[0];
        } else if (u.includes('youtube.com')) {
          const url = new URL(u);
          id = url.searchParams.get('v') || undefined;
        }
      } catch {}
      return {
        key: `m-${idx}-${it.url}`,
        title: it.title || `Video ${idx + 1}`,
        thumbnail_url: it.thumbnail_url,
        published_date: it.uploaded_at,
        video_id: id,
        source: 'manual',
        url: it.url,
      } as PreviewItem;
    });
    const auto: PreviewItem[] = (autoVideos || []).map((v) => ({
      key: `a-${v.video_id}`,
      title: v.title,
      thumbnail_url: v.thumbnail_url,
      published_date: v.published_date,
      video_id: v.video_id,
      source: 'auto',
    }));
    const out: PreviewItem[] = [];
    const seen = new Set<string>();
    const pushUnique = (it: PreviewItem) => {
      const k = it.video_id || it.url || it.key;
      if (!k) return;
      if (seen.has(k)) return;
      seen.add(k);
      out.push(it);
    };
    manual.forEach(pushUnique);
    auto.forEach(pushUnique);
    const filtered = out
      .filter((it) => (it.title || '').toLowerCase().includes(filter.toLowerCase()))
      .slice()
      .sort((a, b) => {
        const ta = Date.parse(a.published_date || '') || 0;
        const tb = Date.parse(b.published_date || '') || 0;
        return tb - ta;
      });
    return { list: filtered, count: filtered.length };
  }, [items, autoVideos, filter]);

  // Auto-disable videos module if there is neither channel nor manual items configured
  useEffect(() => {
    if (loading) return;
    if (!hasChannel && items.length === 0 && videosEnabled) {
      setVideosEnabled(false);
    }
  }, [loading, hasChannel, items.length, videosEnabled]);

  // Auto-trigger backend prefetch of YouTube cache at most once per ~24h
  useEffect(() => {
    if (loading) return;
    const channel = (channelInput || '').trim();
    if (!channel) return;
    const KEY = 'youtube_autoload_last';
    let last = 0;
    try { last = Number(localStorage.getItem(KEY) || '0'); } catch {}
    const DAY_MS = 24 * 60 * 60 * 1000;
    const due = !last || (Date.now() - last) > (23 * 60 * 60 * 1000); // ~23h to allow slight drift
    if (!due) return;
    (async () => {
      try {
        // Ask backend to refresh cached files; it will update youtube_channel.json opportunistically
        await triggerPrefetch();
        try { localStorage.setItem(KEY, String(Date.now())); } catch {}
        toast({ status: 'info', title: 'Aktualizace videí', description: 'Na pozadí se aktualizuje cache videí z YouTube.', duration: 3000 });
      } catch {
        // silent
      }
    })();
  }, [loading, videosSource, channelInput, toast]);

  const refreshAuto = async () => {
    setAutoError('');
    setAutoLoading(true);
    try {
      await triggerPrefetch();
      const payload = await getCachedYouTube();
      setAutoVideos(payload?.videos || []);
      toast({ status: 'success', title: 'Aktualizováno', description: 'Cache videí byla obnovena.', duration: 3000 });
    } catch (e) {
      setAutoError('Aktualizace cache selhala.');
    } finally {
      setAutoLoading(false);
    }
  };

  const saveOverrides = async () => {
    try {
      await updateAdminSettings({ videos_title_overrides: titleOverrides } as any);
      toast({ status: 'success', title: 'Přepisy uloženy', description: 'Názvy videí byly aktualizovány.', duration: 2500 });
    } catch (e) {
      toast({ status: 'error', title: 'Chyba', description: 'Nepodařilo se uložit přepisy názvů.', duration: 3000 });
    }
  };

  const saveAll = async () => {
    setSaving(true);
    try {
      await updateAdminSettings({
        videos_items: items,
        videos_module_enabled: videosEnabled,
        videos_title_overrides: titleOverrides,
      } as any);
      toast({ status: 'success', title: 'Uloženo', description: 'Nastavení videí bylo uloženo.', duration: 2500 });
    } catch (e) {
      toast({ status: 'error', title: 'Chyba', description: 'Nepodařilo se uložit nastavení videí.', duration: 3000 });
    } finally {
      setSaving(false);
    }
  };

  const fetchChannelVideos = async () => {
    const channel = channelInput?.trim();
    if (!channel) {
      toast({ status: 'warning', title: 'Zadejte kanál', description: 'Zadejte YouTube handle nebo URL kanálu.' });
      return;
    }
    setYtError('');
    setYtLoading(true);
    setYtVideos([]);
    setSelectedIds({});
    try {
      const url = `https://youtube.tdvorak.dev/channel_videos?channel=${encodeURIComponent(channel)}`;
      const res = await fetch(url, { method: 'GET' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const vids = Array.isArray(data?.videos) ? data.videos : [];
      setYtVideos(vids);
      if (vids.length === 0) {
        setYtError('Na kanálu nebyla nalezena žádná videa.');
      }
    } catch (err: any) {
      console.error(err);
      setYtError('Nepodařilo se načíst videa z API. Zkontrolujte dostupnost služby na https://youtube.tdvorak.dev/ a CORS.');
    } finally {
      setYtLoading(false);
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const importSelected = async () => {
    const selected = ytVideos.filter((v) => selectedIds[v.video_id]);
    if (selected.length === 0) {
      toast({ status: 'info', title: 'Nic k importu', description: 'Vyberte alespoň jedno video.' });
      return;
    }
    const newItems: AdminVideoItem[] = selected.map((v) => ({
      url: `https://www.youtube.com/watch?v=${v.video_id}`,
      title: v.title,
      length: v.length,
      uploaded_at: (v.published_date || '').slice(0,10),
      thumbnail_url: v.thumbnail_url,
    }));
    // Avoid duplicates by URL
    const merged = (() => {
      const urls = new Set(items.map((p) => p.url));
      const out = [...items];
      for (const it of newItems) {
        if (!urls.has(it.url)) {
          out.push(it);
          urls.add(it.url);
        }
      }
      return out;
    })();
    try {
      await updateAdminSettings({ videos_items: merged, videos_module_enabled: videosEnabled });
      setItems(merged);
      toast({ status: 'success', title: 'Videa přidána', description: `${selected.length} videí bylo přidáno do seznamu.` });
    } catch (e) {
      toast({ status: 'error', title: 'Chyba', description: 'Nepodařilo se uložit přidaná videa.' });
    }
  };

  const addDirectLink = async () => {
    const url = (directUrl || '').trim();
    if (!url) {
      toast({ status: 'warning', title: 'Zadejte odkaz', description: 'Vložte URL videa.' });
      return;
    }
    const today = new Date().toISOString().slice(0,10);
    const resolvedTitle = await getTitleFromUrl(url);
    const it: AdminVideoItem = { url, title: resolvedTitle, uploaded_at: today, thumbnail_url: getThumbFromUrl(url) };
    if (items.find((p) => p.url === it.url)) {
      toast({ status: 'info', title: 'Video už existuje', description: 'Tento odkaz je již v seznamu.' });
      return;
    }
    const merged = [...items, it];
    try {
      await updateAdminSettings({ videos_items: merged, videos_module_enabled: videosEnabled });
      setItems(merged);
      setDirectUrl('');
      toast({ status: 'success', title: 'Video přidáno', description: 'Video bylo přidáno k automatickým videím.' });
    } catch {
      toast({ status: 'error', title: 'Chyba', description: 'Nepodařilo se uložit video.' });
    }
  };

  //

  // Helper: derive YouTube thumbnail safely from a URL (supports youtube.com and youtu.be)
  const getThumbFromUrl = (raw: string): string | undefined => {
    try {
      const u = raw.trim();
      if (!u) return undefined;
      if (u.includes('youtu.be/')) {
        const id = u.split('youtu.be/')[1]?.split(/[?&#]/)[0];
        return id ? `https://i.ytimg.com/vi/${id}/hqdefault.jpg` : undefined;
      }
      if (u.includes('youtube.com')) {
        // Try URL API first
        try {
          const url = new URL(u);
          const id = url.searchParams.get('v') || '';
          if (id) return `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;
        } catch {}
        // Fallback regex
        const m = u.match(/[?&]v=([^&#]+)/);
        const id = m?.[1];
        return id ? `https://i.ytimg.com/vi/${id}/hqdefault.jpg` : undefined;
      }
    } catch {}
    return undefined;
  };

  return (
    <AdminLayout requireAdmin={false}>
      <Box>
        <Heading size="md" mb={2}>Videa (pro titulní stránku)</Heading>
        <Text fontSize="sm" color="gray.600" mb={2}>Přidejte až 5 videí (doporučeno). První se zobrazí jako hlavní, další 4 v mřížce. Podporováno YouTube/Vimeo URL.</Text>

        {/* Source toggle */}
        <HStack justify="space-between" mb={3} flexWrap="wrap">
          <HStack spacing={2}>
            <Button leftIcon={<FiPlus />} colorScheme="green" size="sm" onClick={onOpenAdd}>Přidat video</Button>
          </HStack>
          <FormControl display="flex" alignItems="center" w="auto">
            <FormLabel mb={0}>Zobrazit sekci Videa na titulní stránce</FormLabel>
            <Switch
              isChecked={videosEnabled}
              isDisabled={loading || saving}
              onChange={async (e) => {
                const next = e.target.checked;
                // Require either channel or at least one manual video to enable
                if (next && !hasChannel && items.length === 0) {
                  setVideosEnabled(false);
                  toast({ status: 'warning', title: 'Doplňte kanál nebo videa', description: 'Pro zobrazení sekce vyplňte YouTube kanál nebo přidejte alespoň jedno video.', duration: 4000 });
                  // Try to focus channel input
                  setTimeout(() => {
                    const el = document.getElementById('admin-videos-channel-input') as HTMLInputElement | null;
                    if (el) el.focus();
                  }, 0);
                  return;
                }
                setVideosEnabled(next);
                try {
                  await updateAdminSettings({ videos_module_enabled: next });
                } catch {
                  toast({ status: 'error', title: 'Uložení selhalo', description: 'Nepodařilo se uložit změnu zobrazení sekce.', duration: 3000 });
                }
              }}
            />
          </FormControl>
        </HStack>
        {!hasChannel && items.length === 0 && (
          <Text fontSize="sm" color="orange.600" mb={2}>Pro aktivaci sekce vyplňte YouTube kanál nebo přidejte video.</Text>
        )}
        {videosSource === 'auto' && (
          <Alert status="info" mb={3} borderRadius="md">
            <AlertIcon />
            Automatický režim je zapnutý. Videa se načítají z YouTube kanálu z Nastavení → Sociální sítě (YouTube URL). Ručně přidaná videa se zobrazí před automatickými.
          </Alert>
        )}

        

        {/* Always-visible preview of effective videos */}
        <Box borderWidth="1px" borderRadius="md" p={3} mb={4}>
          <HStack justify="space-between" align="center" mb={2} flexWrap="wrap">
            <Heading size="sm">Náhled: všechna videa (aktivní zdroj)</Heading>
            {videosSource === 'auto' && (
              <HStack spacing={2} flexWrap="wrap">
                <Input size="sm" placeholder="Filtrovat podle názvu" value={filter} onChange={(e) => setFilter(e.target.value)} width={{ base: '100%', md: '260px' }} />
                <Button size="sm" onClick={refreshAuto} isLoading={autoLoading} variant="outline" flexShrink={0} minW="max-content">Aktualizovat cache</Button>
                <Button size="sm" colorScheme="blue" variant="solid" onClick={saveOverrides} flexShrink={0} minW="max-content">Uložit přepisy názvů</Button>
              </HStack>
            )}
          </HStack>
          {videosSource === 'auto' ? (
            <>
              {autoError && (
                <Alert status="error" mb={2} borderRadius="md"><AlertIcon />{autoError}</Alert>
              )}
              {autoLoading ? (
                <HStack color="gray.600"><Spinner size="sm" /><Text>Načítám videa…</Text></HStack>
              ) : (
                <>
                  <Text fontSize="sm" color="gray.600" mb={2}>Počet videí: {combinedAutoPreview.count}</Text>
                  <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 4 }} spacing={3}>
                    {combinedAutoPreview.list.map((it) => (
                      <Box key={it.key} borderWidth="1px" borderRadius="md" p={2}>
                        <VStack align="stretch" spacing={2}>
                          <Image
                            src={it.thumbnail_url || (it.source === 'manual' ? getThumbFromUrl(it.url || '') : undefined)}
                            alt={it.title}
                            borderRadius="md"
                            data-fallback-idx={0 as any}
                            onError={(e) => {
                              const el = e.currentTarget as HTMLImageElement & { dataset: { fallbackIdx?: string } };
                              const idx = Number(el.dataset.fallbackIdx || '0');
                              const id = it.video_id || '';
                              const chain = id ? [
                                `https://i.ytimg.com/vi/${id}/mqdefault.jpg`,
                                `https://i.ytimg.com/vi/${id}/sddefault.jpg`,
                                `https://i.ytimg.com/vi/${id}/hqdefault.jpg`,
                                '/images/sponsors/placeholder.png',
                              ] : ['/images/sponsors/placeholder.png'];
                              if (idx < chain.length) {
                                el.src = chain[idx];
                                el.dataset.fallbackIdx = String(idx + 1);
                              }
                            }}
                          />
                          <Box>
                            <HStack justify="space-between" align="start">
                              <Box flex="1">
                                <Text fontWeight="semibold" noOfLines={2}>{(it.source === 'auto' && it.video_id && (titleOverrides[it.video_id]?.trim()?.length ? titleOverrides[it.video_id] : it.title)) || it.title}</Text>
                                <HStack spacing={2} color="gray.600" fontSize="sm">
                                  {it.published_date && <Badge>{new Date(it.published_date).toLocaleDateString('cs-CZ')}</Badge>}
                                  {it.source === 'manual' && <Badge colorScheme="purple">Ručně</Badge>}
                                </HStack>
                              </Box>
                              {it.source === 'manual' && (
                                <IconButton
                                  aria-label="Smazat"
                                  icon={<FiTrash2 />}
                                  size="sm"
                                  variant="ghost"
                                  colorScheme="red"
                                  onClick={async () => {
                                    const next = items.filter((m) => m.url !== it.url);
                                    try {
                                      await updateAdminSettings({ videos_items: next, videos_module_enabled: videosEnabled });
                                      setItems(next);
                                      toast({ status: 'success', title: 'Smazáno', description: 'Video bylo odstraněno.' });
                                    } catch {
                                      toast({ status: 'error', title: 'Chyba', description: 'Odstranění se nepodařilo.' });
                                    }
                                  }}
                                />
                              )}
                            </HStack>
                            {it.source === 'auto' && it.video_id && (
                              <FormControl mt={2}>
                                <FormLabel fontSize="xs" mb={1}>Přepis názvu (volitelné)</FormLabel>
                                <Input
                                  size="sm"
                                  placeholder="Např. Zápas A‑týmu vs. B‑tým"
                                  value={titleOverrides[it.video_id] ?? ''}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    setTitleOverrides(prev => ({ ...prev, [it.video_id!]: val }));
                                  }}
                                />
                              </FormControl>
                            )}
                            {it.source === 'manual' && it.url && (
                              <FormControl mt={2}>
                                <FormLabel fontSize="xs" mb={1}>Název videa</FormLabel>
                                <Input
                                  size="sm"
                                  placeholder="Zadejte vlastní název"
                                  value={(items.find(p => p.url === it.url)?.title) ?? ''}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    setItems(prev => prev.map(p => p.url === it.url ? { ...p, title: val } : p));
                                  }}
                                  onBlur={async (e) => {
                                    const val = e.target.value;
                                    const next = items.map(p => p.url === it.url ? { ...p, title: val } : p);
                                    try {
                                      await updateAdminSettings({ videos_items: next, videos_module_enabled: videosEnabled });
                                      setItems(next);
                                    } catch {}
                                  }}
                                />
                              </FormControl>
                            )}
                          </Box>
                        </VStack>
                      </Box>
                    ))}
                  </SimpleGrid>
                  {combinedAutoPreview.count === 0 && (
                    <Text color="gray.600">Zatím žádná videa.</Text>
                  )}
                </>
              )}
            </>
          ) : null}
        </Box>

        <HStack justify="flex-end" mb={3}>
          <Button colorScheme="blue" leftIcon={<FiSave />} onClick={saveAll} isLoading={saving}>
            Uložit vše
          </Button>
        </HStack>

        <Modal isOpen={isAddOpen} onClose={onCloseAdd} size="4xl">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Přidat video</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Tabs variant="enclosed">
                <TabList>
                  <Tab>Odkaz na video</Tab>
                  <Tab>Načíst z YouTube kanálu</Tab>
                </TabList>
                <TabPanels>
                  <TabPanel>
                    <VStack align="stretch" spacing={3}>
                      <FormControl>
                        <FormLabel>URL videa</FormLabel>
                        <Input placeholder="https://www.youtube.com/watch?v=..." value={directUrl} onChange={(e) => setDirectUrl(e.target.value)} />
                      </FormControl>
                      <HStack>
                        <Button colorScheme="green" onClick={async () => { await addDirectLink(); onCloseAdd(); }}>Přidat</Button>
                      </HStack>
                    </VStack>
                  </TabPanel>
                  <TabPanel>
                    <VStack align="stretch" spacing={3}>
                      <Text fontSize="sm" color="gray.600">
                        Použijte Scraper API. Zadejte handle (např. <code>@FotbalKunovice</code>) nebo URL kanálu a načtěte videa z karty „Videa“. Služba: <Link href="https://youtube.tdvorak.dev/" isExternal color="blue.500">youtube.tdvorak.dev</Link>
                      </Text>
                      <HStack align="start" spacing={3} flexWrap="wrap">
                        <FormControl maxW={{ base: '100%', md: '400px' }}>
                          <FormLabel>Kanál (handle nebo URL)</FormLabel>
                          <Input id="admin-videos-channel-input" placeholder="@FCBizoniUH nebo https://www.youtube.com/@FCBizoniUH/videos" value={channelInput} onChange={(e) => setChannelInput(e.target.value)} />
                        </FormControl>
                        <Button onClick={fetchChannelVideos} isLoading={ytLoading} variant="outline" flexShrink={0} minW="max-content">Načíst videa</Button>
                        <Button colorScheme="green" onClick={async () => { await importSelected(); onCloseAdd(); }} isDisabled={ytVideos.length === 0} flexShrink={0} minW="max-content">Přidat vybraná</Button>
                      </HStack>
                      {ytError && (
                        <Alert status="error" borderRadius="md">
                          <AlertIcon />
                          {ytError}
                        </Alert>
                      )}
                      {ytLoading && (
                        <HStack color="gray.600"><Spinner size="sm" /><Text>Načítám videa…</Text></HStack>
                      )}
                      {!ytLoading && ytVideos.length > 0 && (
                        <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 4 }} spacing={3}>
                          {ytVideos.map((v) => (
                            <Box key={v.video_id} borderWidth="1px" borderRadius="md" p={2}>
                              <VStack align="stretch" spacing={2}>
                                <Image src={v.thumbnail_url} alt={v.title} borderRadius="md" />
                                <Checkbox isChecked={!!selectedIds[v.video_id]} onChange={() => toggleSelect(v.video_id)}>
                                  Vybrat
                                </Checkbox>
                                <Box>
                                  <Text fontWeight="semibold" noOfLines={2}>{v.title}</Text>
                                  <HStack spacing={2} color="gray.600" fontSize="sm">
                                    {v.length && <Badge>{v.length}</Badge>}
                                    {v.published_text && <Text>{v.published_text}</Text>}
                                  </HStack>
                                </Box>
                              </VStack>
                            </Box>
                          ))}
                        </SimpleGrid>
                      )}
                    </VStack>
                  </TabPanel>
                </TabPanels>
              </Tabs>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" onClick={onCloseAdd}>Zavřít</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Box>
    </AdminLayout>
  );
};

export default AdminVideosPage;
